import { useState } from 'react';
import { motion } from 'framer-motion';
import { Toaster } from 'sonner';
import {
  Twitter, Sparkles, Calendar, Coins, Layers, Lock,
  Rocket, Gift, Users, Trophy, Palette, Zap,
} from 'lucide-react';
import { WaitlistDialog }  from './components/WaitlistDialog';
import SocialShareCards     from './components/SocialShareCards';

/* ── Asset URLs (replace with your own CDN/public paths) ─────── */
const HERO_IMAGE =
  'https://project--e2707d98-7eb2-439c-818e-baebe8b9edeb.lovable.app' +
  '/__l5e/assets-v1/dd70c7ca-7c8a-4246-917a-f68c0b5f23de/glummies-hero.png';

const ART_VIDEO =
  'https://res.cloudinary.com/ijjybdwk/video/upload/v1783524524/GLUMMIES_GIF_qpazdf.mp4';

/* ── Data ─────────────────────────────────────────────────────── */
const stats = [
  { icon: Layers,   label: 'Supply',    value: '2222'    },
  { icon: Coins,    label: 'Mint Price', value: 'TBA'     },
  { icon: Calendar, label: 'Mint Date',  value: 'TBA'     },
  { icon: Sparkles, label: 'Launchpad',  value: 'OpenSea' },
];

const MARQUEE_ITEMS = ['GLUMMIES', '2222 SUPPLY', 'ROBINHOOD CHAIN', 'MINT ON OPENSEA', 'WAGMI', 'GLUMMIES'];
const ROADMAP_ICONS = [Rocket, Gift, Users, Palette, Trophy, Zap];

/* ── App (= former routes/index.tsx Home component) ──────────── */
export default function App() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="min-h-screen bg-hero overflow-hidden font-body">
      <Toaster richColors position="top-center" />

      {/* ── Nav ──────────────────────────────────────────────── */}
      <header className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
        <div className="text-2xl font-bold rainbow-text">GLUMMIES</div>

        <nav className="flex items-center gap-6 text-sm font-bold">
          <a href="#collection" className="hidden sm:inline hover:text-primary transition-colors">
            Collection
          </a>
          <a href="#waitlist" className="hidden sm:inline hover:text-primary transition-colors">
            Whitelist
          </a>
          <a href="#art" className="hidden sm:inline hover:text-primary transition-colors">
            Art
          </a>
          <a
            href="https://x.com/Glummies_"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border-2 border-ink bg-card p-2 shadow-pop"
            aria-label="X / Twitter"
          >
            <Twitter className="h-4 w-4" />
          </a>
        </nav>
      </header>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 pt-4 pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-card px-4 py-1.5 text-xs font-bold shadow-pop"
        >
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          Launching on Robinhood Chain
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-5xl sm:text-7xl md:text-8xl font-bold"
          style={{ lineHeight: 0.95 }}
        >
          Meet the <span className="rainbow-text">Glummies</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mx-auto mt-5 max-w-xl text-base sm:text-lg text-muted-foreground"
        >
          2222 hand-drawn little dudes with big vibes. Minting on OpenSea.
          Don't be glum — get one.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 flex flex-wrap justify-center gap-3"
        >
          <a
            href="#waitlist"
            className="rounded-full border-2 border-ink bg-foreground px-7 py-3 font-bold text-card shadow-pop transition hover:-translate-y-0.5"
          >
            Secure your spot
          </a>
          <a
            href="#collection"
            className="rounded-full border-2 border-ink bg-card px-7 py-3 font-bold shadow-pop transition hover:-translate-y-0.5"
          >
            Learn more
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mx-auto mt-16 max-w-5xl"
        >
          <div className="rounded-[2.5rem] border-2 border-ink bg-card p-3 shadow-glum animate-float-slow">
            <img
              src={HERO_IMAGE}
              alt="Glummies NFT collection characters"
              className="w-full rounded-[2rem]"
              loading="eager"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </motion.div>
      </section>

      {/* ── Marquee ticker ───────────────────────────────────── */}
      <div className="border-y-2 border-ink bg-foreground py-4 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap text-2xl font-bold text-card">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-8 px-4">
              {MARQUEE_ITEMS.map((t) => (
                <span key={`${i}-${t}`} className="flex items-center gap-8">
                  {t} <Sparkles className="h-5 w-5 text-glum-yellow" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Collection details ────────────────────────────────── */}
      <section id="collection" className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center">
          <h2 className="text-4xl sm:text-5xl font-bold">
            Collection <span className="rainbow-text">Details</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Everything you need to know before mint day.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              whileHover={{ y: -6, rotate: -1 }}
              className="rounded-3xl border-2 border-ink bg-card p-6 shadow-pop"
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-ink bg-primary text-primary-foreground">
                <s.icon className="h-6 w-6" />
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {s.label}
              </div>
              <div className="mt-1 text-3xl font-bold">{s.value}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Waitlist CTA ──────────────────────────────────────── */}
      <section id="waitlist" className="mx-auto max-w-4xl px-6 py-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-[2.5rem] border-2 border-ink bg-card p-10 sm:p-16 text-center shadow-pop"
        >
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-glum-orange opacity-40 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-glum-blue opacity-40 blur-3xl" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-glum-yellow px-4 py-1 text-xs font-bold">
              WHITELIST OPEN
            </div>
            <h2 className="mt-5 text-4xl sm:text-6xl font-bold">
              Secure your <span className="rainbow-text">Spot</span>
            </h2>
            <p className="mx-auto mt-4 max-w-md text-muted-foreground">
              Drop your wallet, prove you're not a bot, and shout it on X.
              That's it.
            </p>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setDialogOpen(true)}
              className="mt-8 rounded-full border-2 border-ink bg-foreground px-10 py-4 text-lg font-bold text-card shadow-pop"
            >
              Join the Whitelist →
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* ── Art ──────────────────────────────────────────────── */}
      <section id="art" className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center">
          <h2 className="text-4xl sm:text-5xl font-bold">
            Our <span className="rainbow-text">Arts</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            A little peek at the Glum-verse in motion.
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto mt-12 max-w-4xl rounded-[2.5rem] border-2 border-ink bg-card p-3 shadow-glum"
        >
          <video
            src={ART_VIDEO}
            autoPlay
            loop
            muted
            playsInline
            className="w-full rounded-[2rem]"
          />
        </motion.div>
      </section>

      {/* ── Roadmap & Utilities — 6 locked phase cards ───────── */}
      <section id="roadmap" className="mx-auto max-w-6xl px-6 pb-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-card px-4 py-1 text-xs font-bold uppercase tracking-widest shadow-pop">
            <Lock className="h-3.5 w-3.5" /> Sealed
          </div>
          <h2 className="mt-5 text-4xl sm:text-5xl font-bold">
            Roadmap &amp; <span className="rainbow-text">Utilities</span>
          </h2>
          <p className="mx-auto mt-3 max-w-md text-muted-foreground">
            The vault is sealed. Reveal drops with the mint — stay glummed in.
          </p>
        </div>

        <div className="relative mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ROADMAP_ICONS.map((Icon, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-3xl border-2 border-ink bg-card p-6 shadow-pop"
              aria-label="Locked phase"
            >
              {/* Decorative gradient orb */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-glum-pink via-glum-orange to-glum-yellow opacity-40 blur-2xl" />

              <div className="relative flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-ink bg-foreground text-card">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="rounded-full border-2 border-ink bg-background px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                  Phase {String(i + 1).padStart(2, '0')}
                </div>
              </div>

              {/* Redacted bars (decorative, no real content) */}
              <div className="relative mt-6 space-y-2" aria-hidden="true">
                <div className="h-3 w-3/4 rounded-full bg-ink/80" />
                <div className="h-3 w-full rounded-full bg-ink/60" />
                <div className="h-3 w-2/3 rounded-full bg-ink/40" />
              </div>

              <div className="relative mt-6 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-glum-yellow px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                  <Lock className="h-3 w-3" /> Locked
                </div>
                <div className="text-xs font-bold text-muted-foreground">?? / ??</div>
              </div>

              {/* Frosted lock overlay */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-card/40 backdrop-blur-[3px] transition-all group-hover:backdrop-blur-md">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-ink bg-foreground text-card shadow-pop">
                  <Lock className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Social Share Cards (X · WhatsApp · Telegram · Discord · Reddit) */}
      <SocialShareCards />

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="border-t-2 border-ink bg-foreground text-card">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-6 py-12 sm:flex-row sm:justify-between">
          <div>
            <div className="text-2xl font-bold rainbow-text">GLUMMIES</div>
            <p className="mt-1 text-sm opacity-70">
              © {new Date().getFullYear()} Glummies. All vibes reserved.
            </p>
          </div>
          <a
            href="https://x.com/Glummies_"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-full border-2 border-background bg-transparent px-5 py-3 font-bold transition hover:bg-card hover:text-foreground"
          >
            <Twitter className="h-5 w-5" /> @Glummies_
          </a>
        </div>
      </footer>

      {/* ── Waitlist modal ────────────────────────────────────── */}
      <WaitlistDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}